import {flow, getEnv, types} from "mobx-state-tree";
import {GroupSelectState} from "../../../elements/groups/GroupSelectState";
import {ParameterSetSelectState} from "../../../elements/parameterSets/ParameterSetSelectState";
import {CodeParamsTypeSelectState} from "../../../elements/parameters/codeType/CodeParamsTypeSelectState";
import {ScanParamsTypeSelectState} from "../../../elements/parameters/scanType/ScanParamsTypeSelectState";
import {CountParamsTypeSelectState} from "../../../elements/parameters/countType/CountParamsTypeSelectState";
import {PremiumParamsTypeSelectState} from "../../../elements/parameters/premiumType/PremiumParamsTypeSelectState";
import {CustomParamsTypeSelectState} from "../../../elements/parameters/customType/CustomParamsTypeSelectState";
import {GS1ParamsTypeSelectState} from "../../../elements/parameters/gs1Type/GS1ParamsTypeSelectState";
import {ExtendedDataSelectState} from "../../../extendedData/ExtendedDataSelectState";


export const ParameterSetModalState = types.model("ParameterSetModalState", {
  isLoading: types.optional(types.boolean, false),
  groupSelectStore: types.maybe(types.reference(GroupSelectState)),
  noParameterSetOpen:types.optional(types.boolean, false),
  customOpen:types.optional(types.boolean, true),
  parameterSetSelectState:types.maybe(types.reference(ParameterSetSelectState)),

  codeParamsTypeSelectState: types.maybe(CodeParamsTypeSelectState),
  scanParamsTypeSelectState: types.maybe(ScanParamsTypeSelectState),
  countParamsTypeSelectState: types.maybe(CountParamsTypeSelectState),
  premiumParamsTypeSelectState: types.maybe(PremiumParamsTypeSelectState),
  customParamsTypeSelectState: types.maybe(CustomParamsTypeSelectState),
  gs1ParamsTypeSelectState: types.maybe(GS1ParamsTypeSelectState),
  parameterExtendedDataSelectStore: types.maybe(types.late(() => types.reference(ExtendedDataSelectState))),
  isView: types.optional(types.boolean, false),
  isRemovedParameterSet: types.optional(types.boolean, false),
  batchCodeParamTypeLock: types.optional(types.boolean, false), // those are only for batch
  batchScanParamTypeLock: types.optional(types.boolean, false),
  batchCountParamTypeLock: types.optional(types.boolean, false),
  batchPremiumParamTypeLock: types.optional(types.boolean, false),
  batchCustomParamTypeLock: types.optional(types.boolean, false),
  batchGS1ParamTypeLock: types.optional(types.boolean, false),
  batchEDParamTypeLock: types.optional(types.boolean, false),
})
  .actions(self => {
    return ({

      setNoneOpen() {
        self.noParameterSetOpen = true
        self.presetsOpen = false
        self.customOpen = false
      },
      selectOnChanges() {
        self.parameterExtendedDataSelectStore.setConsumer(self.selectExtendedData);
      },
      selectExtendedData(arr) {
        self.parameterExtendedDataSelectStore.setSelectedExtendedData(arr);
      },
      setCustomOpen() {
        self.noParameterSetOpen = false
        self.presetsOpen = false
        self.customOpen = true
      },
      consumeParameterSetSelect() {
        self.parameterSetSelectState.setConsumer(self.fetchParameterSetWithID)
      },
      hydrateForm(parameterSet) {
        self.codeParamsTypeSelectState.selectParameters(parameterSet.parameters?.codeParamType)
        self.scanParamsTypeSelectState.selectParameters(parameterSet.parameters?.scanParamType)
        self.countParamsTypeSelectState.selectParameters(parameterSet.parameters?.countParamType)
        self.premiumParamsTypeSelectState.selectParameters(parameterSet.parameters?.premiumParamType)
        self.customParamsTypeSelectState.selectParameters(parameterSet.parameters?.customParamType)
        self.gs1ParamsTypeSelectState.selectParameters(parameterSet.parameters?.gs1ParamType)
        self.parameterExtendedDataSelectStore.setSelectedExtendedData(parameterSet?.parameters?.edParamType)
        self.generateBatchLocks(parameterSet.parameters)
      },
      fetchParameterSetWithID: flow(function* f(uuid) {
        self.isLoading = true
        const parameterSetManager = getEnv(self).parameterSetManager
        const res = yield parameterSetManager.getParameterSet(uuid)
        const parameterSet = {
          parameters:res
        }
        self.hydrateForm(parameterSet)
        self.isLoading = false
      }),
      mapParametersToSelectState(params) {
        params.forEach(p => {
          switch (p?.paramType) {
            case "codeParams":
              self.codeParamsTypeSelectState.store?.setList(p?.items)
              break;
            case "scanParams":
              self.scanParamsTypeSelectState.store?.setList(p?.items)
              break;
            case "countParams":
              self.countParamsTypeSelectState.store?.setList(p?.items)
              break;
            case "premiumParams":
              self.premiumParamsTypeSelectState.store?.setList(p?.items)
              break;
            case "customParams":
              self.customParamsTypeSelectState.store?.setList(p?.items)
              break;
            case "gs1Params":
              self.gs1ParamsTypeSelectState.store?.setList(p?.items);
            default:
              break;
          }
        })
      },
      fetchAllParameters: flow(function* f() {
        const manager = getEnv(self).parametersManager
        const resp = yield manager.fetchAll();
        if (resp?.results) {
          self.mapParametersToSelectState(resp?.results)
        }
      }),
      clearParameters(customOpen = true) {
        self.codeParamsTypeSelectState.selectedCodeParameters = []
        self.scanParamsTypeSelectState.selectedScanParameters = []
        self.countParamsTypeSelectState.selectedCountParameters = []
        self.premiumParamsTypeSelectState.selectedPremiumParameters = []
        self.customParamsTypeSelectState.selectedCustomParameters = []
        self.gs1ParamsTypeSelectState.selectedGS1Parameters = []
        self.parameterExtendedDataSelectStore.setSelectedExtendedData([])
        self.noParameterSetOpen = !customOpen
        self.customOpen = customOpen
        self.isRemovedParameterSet = false
        self.batchCodeParamTypeLock = false
        self.batchScanParamTypeLock = false
        self.batchCountParamTypeLock = false
        self.batchPremiumParamTypeLock = false
        self.batchCustomParamTypeLock = false
        self.batchGS1ParamTypeLock = false
        self.batchEDParamTypeLock = false
      },
      setOpenView() {
        if(self.doesParamsHaveValues()) {
          self.setCustomOpen()
        }
      },
      generateBatchLocks(parameters) {
        self.batchCodeParamTypeLock = parameters.batchCodeParamTypeLock
        self.batchScanParamTypeLock = parameters.batchScanParamTypeLock
        self.batchCountParamTypeLock = parameters.batchCountParamTypeLock
        self.batchPremiumParamTypeLock = parameters.batchPremiumParamTypeLock
        self.batchCustomParamTypeLock = parameters.batchCustomParamTypeLock
        self.batchGS1ParamTypeLock = parameters.batchGS1ParamTypeLock
        self.batchEDParamTypeLock = parameters.batchEDParamTypeLock
      },
      setParametersCodeParamsTypeDisabled(bool) {
        self.batchCodeParamTypeLock = !bool
        if(!bool) {
          self.codeParamsTypeSelectState.selectedCodeParameters = []
        }
      },
      setParametersScanParamsTypeDisabled(bool) {
        self.batchScanParamTypeLock = !bool
        if(!bool) {
          self.scanParamsTypeSelectState.selectedScanParameters = []
        }
      },
      setParametersCountParamsTypeDisabled(bool) {
        self.batchCountParamTypeLock = !bool
        if(!bool) {
          self.countParamsTypeSelectState.selectedCountParameters = []
        }
      },
      setParametersPremiumParamsTypeDisabled(bool) {
        self.batchPremiumParamTypeLock = !bool
        if(!bool) {
          self.premiumParamsTypeSelectState.selectedPremiumParameters = []
        }
      },
      setParametersCustomParamsTypeDisabled(bool) {
        self.batchCustomParamTypeLock = !bool
        if(!bool) {
          self.customParamsTypeSelectState.selectedCustomParameters = []
        }
      },
      setParametersGS1ParamsTypeDisabled(bool) {
        self.batchGS1ParamTypeLock = !bool
        if(!bool) {
          self.gs1ParamsTypeSelectState.selectedGS1Parameters = []
        }
      },
      setParametersEDParamsTypeDisabled(bool) {
        self.batchEDParamTypeLock = !bool
        if(!bool) {
          self.parameterExtendedDataSelectStore.setSelectedExtendedData([])
        }
      },
      resetCodeParams() {
        self.codeParamsTypeSelectState.selectedCodeParameters = []
        self.batchCodeParamTypeLock = false
      },
      resetScanParams() {
        self.scanParamsTypeSelectState.selectedScanParameters = []
        self.batchScanParamTypeLock = false
      },
      resetCountParams() {
        self.countParamsTypeSelectState.selectedCountParameters = []
        self.batchCountParamTypeLock = false
      },
      resetPremiumParams() {
        self.premiumParamsTypeSelectState.selectedPremiumParameters = []
        self.batchPremiumParamTypeLock = false
      },
      resetCustomParams() {
        self.customParamsTypeSelectState.selectedCustomParameters = []
        self.batchCustomParamTypeLock = false
      },
      resetGS1Params() {
        self.gs1ParamsTypeSelectState.selectedGS1Parameters = []
        self.batchGS1ParamTypeLock = false
      },
      resetEDParams() {
        self.parameterExtendedDataSelectStore.setSelectedExtendedData([])
        self.batchEDParamTypeLock = false
      }
    });
  }).views((self) => ({
    doesParamsHaveValues() {
      return self.codeParamsTypeSelectState.selectedCodeParameters.length > 0 || self.scanParamsTypeSelectState.selectedScanParameters.length > 0
        || self.gs1ParamsTypeSelectState.selectedGS1Parameters.length > 0 || self.parameterExtendedDataSelectStore.selectedExtendedData.length > 0
        || self.countParamsTypeSelectState.selectedCountParameters.length > 0 || self.premiumParamsTypeSelectState.selectedPremiumParameters.length > 0
        || self.customParamsTypeSelectState.selectedCustomParameters.length > 0 || self.batchCodeParamTypeLock || self.batchScanParamTypeLock
        || self.batchCountParamTypeLock || self.batchPremiumParamTypeLock || self.batchCustomParamTypeLock || self.batchGS1ParamTypeLock
        || self.batchEDParamTypeLock
    }
  }))
// toastr.error(`Failure`, `${self.errorMessage}`)
